/**
 * Controls at --control-h (spec Decision 4: "Inputs, selects and segmented controls share the
 * height so toolbars line up"). Every control's root carries data-kit="input" | "select" |
 * "segmented" | "search" — the conformance test records them under `controls`, never as buttons.
 * A textarea (data-kit="textarea") grows with its text and a checkbox (data-kit="checkbox") is 14px:
 * kit parts, not --control-h controls.
 */
import * as React from 'react';
import { Select as BaseSelect } from '@base-ui-components/react/select';
import { Check, ChevronDown, Search as SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input ref={ref} data-kit="input" className={cx('k-input', className)} {...props} />;
});

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, style, rows, ...props }, ref) {
  const sized = rows ? ({ ['--rows' as string]: rows, ...style } as React.CSSProperties) : style;
  return <textarea ref={ref} data-kit="textarea" className={cx('k-input', className)} rows={rows} style={sized} {...props} />;
});

export function Checkbox({ className, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return <input type="checkbox" data-kit="checkbox" className={cx('k-check', className)} {...props} />;
}

/** The form cell (and, inside a FieldGrid, the label/value row) lives in Field.tsx (R16 T-137). */
export { Field } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
}

export interface SelectProps {
  value: string | null | undefined;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  'data-testid'?: string;
}

/** A closed list. The trigger shows the chosen label, never the raw value. */
export function Select({ value, onValueChange, options, placeholder = 'Choose…', id, disabled, className, ...rest }: SelectProps) {
  const current = options.find((o) => o.value === value);
  return (
    <BaseSelect.Root value={value ?? null} onValueChange={(next: string | null) => next !== null && onValueChange(next)} disabled={disabled}>
      <BaseSelect.Trigger
        id={id}
        data-kit="select"
        className={cx('k-select', className)}
        aria-label={rest['aria-label']}
        aria-labelledby={rest['aria-labelledby']}
        data-testid={rest['data-testid']}
        title={current?.hint}
      >
        <span className="k-select-value">{current ? current.label : placeholder}</span>
        <ChevronDown aria-hidden />
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={4} align="start" alignItemWithTrigger={false} collisionPadding={8} className="k-pos">
          <BaseSelect.Popup className="k-pop" data-kit="popover" data-variant="menu">
            <BaseSelect.List>
              {options.map((o) => (
                <BaseSelect.Item key={o.value} value={o.value} disabled={o.disabled} title={o.hint} className="k-pop-item">
                  <BaseSelect.ItemText>{o.label}</BaseSelect.ItemText>
                  <BaseSelect.ItemIndicator style={{ marginLeft: 'auto' }}>
                    <Check size={14} aria-hidden />
                  </BaseSelect.ItemIndicator>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}

/** Two to five mutually exclusive choices shown at once (Department · App · Both). */
export function Segmented<T extends string>({ value, onValueChange, options, 'aria-label': label, className }: {
  value: T;
  onValueChange: (v: T) => void;
  /** R-15 #899: a segment may carry a testid (rule 33: locators by key, not words) */
  options: ReadonlyArray<{ value: T; label: string; testId?: string }>;
  'aria-label': string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} data-kit="segmented" className={cx('k-seg', className)}>
      {options.map((o) => (
        <button key={o.value} type="button" data-kit="segment" data-testid={o.testId} aria-pressed={o.value === value} onClick={() => onValueChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * R-15 T-107 — the segmented control for places: two to five pages shown at once as persistent
 * links, the current one highlighted (aria-current="page"). The page's name never flips; each
 * segment always opens its own page. Tab moves between segments; Enter opens one.
 */
export function SegmentedNav({ current, options, 'aria-label': label, className }: {
  /** the `to` of the page on screen */
  current: string;
  options: ReadonlyArray<{ to: string; label: string; testId?: string }>;
  'aria-label': string;
  className?: string;
}) {
  return (
    <nav aria-label={label} data-kit="segmented" data-variant="nav" className={cx('k-seg', className)}>
      {options.map((o) => (
        <Link key={o.to} to={o.to} data-kit="segment" data-testid={o.testId} aria-current={o.to === current ? 'page' : undefined}>
          {o.label}
        </Link>
      ))}
    </nav>
  );
}

/** Toolbar search: one input with its icon, at --control-h. */
export const Search = React.forwardRef<HTMLInputElement, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { 'aria-label': string }>(function Search({ className, ...props }, ref) {
  return (
    <label data-kit="search" className={cx('k-search', className)}>
      <SearchIcon aria-hidden />
      <input ref={ref} type="search" {...props} />
    </label>
  );
});
